import { type TGlobalFunctionTyped } from 'src/core/common/hooks/useTranslation';

import {
  type ApprovalApproverExport,
  type ApprovalRangeExport,
  type ApprovalSchemeExport,
} from './approvalWorkflowExportTypes';
import {
  buildCell,
  buildRow,
  buildTable,
  type Table,
} from '../spreadsheet/spreadsheetModel';

const getUpToAmount = (range: ApprovalRangeExport): string | undefined =>
  range.type === 'upTo' || range.type === 'fromUpTo' ? range.upTo : undefined;

const getFromAmount = (range: ApprovalRangeExport): string | undefined =>
  range.type === 'upTo' || range.type === 'from' || range.type === 'fromUpTo'
    ? range.from
    : undefined;

const getApproverName = (
  approver: ApprovalApproverExport,
): string | undefined => (approver.type === 'user' ? approver.name : undefined);

const getApproverRole = (
  approver: ApprovalApproverExport,
): string | undefined => (approver.type === 'role' ? approver.role : undefined);

const buildTextCell = (content?: string) => buildCell({ content });

const buildMoneyCell = (amount?: string) =>
  buildCell({ content: amount, alignment: 'right' });

export const exportApprovalSchemesAsFlatTable = (
  approvalSchemes: ApprovalSchemeExport[],
  t: TGlobalFunctionTyped,
): Table => {
  const headerRow = buildRow(
    [
      t('approvalWorkflows.export.approvalSchemeId'),
      t('approvalWorkflows.export.amountFromTitle'),
      t('approvalWorkflows.export.amountUpToTitle'),
      t('approvalWorkflows.export.approvalOrder'),
      t('approvalWorkflows.export.approverName'),
      t('approvalWorkflows.export.approverRole'),
    ].map((content) => buildCell({ content })),
  );

  const schemeRows = approvalSchemes.flatMap((scheme) =>
    scheme.rules.flatMap((rule) =>
      rule.approvers.map((approver, approverIndex) =>
        buildRow([
          buildTextCell(scheme.id),
          buildMoneyCell(getFromAmount(rule.range)),
          buildMoneyCell(getUpToAmount(rule.range)),
          buildTextCell(
            rule.type === 'allInOrder' ? `${approverIndex + 1}` : undefined,
          ),
          buildTextCell(getApproverName(approver)),
          buildTextCell(getApproverRole(approver)),
        ]),
      ),
    ),
  );

  return buildTable({
    headerRowCount: 1,
    frozenRowCount: 1,
    rows: [headerRow, ...schemeRows],
  });
};
